/*
  FamilyShareToggle — small panel on Rx detail. Shows family share status
  and lets the owner flip per-Rx sharing on/off. Hides when the user has
  no family or feature_accounts is off.
*/
import { useEffect, useState } from "react";
import { Users, Loader2, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { family as familyApi, rxShare } from "../lib/accountApi";
import { useAuth } from "../contexts/AuthContext";
import { isFeatureEnabled } from "./FeatureGate";
import { toast } from "sonner";

export default function FamilyShareToggle({ rx, onChanged }) {
  const { user, loading } = useAuth();
  const [enabled, setEnabled] = useState(isFeatureEnabled("feature_accounts"));
  const [fam, setFam] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = () => setEnabled(isFeatureEnabled("feature_accounts"));
    window.addEventListener("vh-config-loaded", h);
    return () => window.removeEventListener("vh-config-loaded", h);
  }, []);

  useEffect(() => {
    if (user) familyApi.me().then(setFam).catch(() => setFam(null));
  }, [user]);

  if (!enabled || loading || !user || !fam) return null;

  const isShared = !!rx?.is_shared;

  async function toggle() {
    setBusy(true);
    try {
      await rxShare.toggle(rx.id, !isShared);
      toast.success(isShared ? "Made private" : `Shared with ${fam.name}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change");
    } finally { setBusy(false); }
  }

  return (
    <div
      data-testid="family-share-toggle"
      className="card-tactile rounded-2xl p-4 flex items-center gap-3"
      style={{ background: isShared ? "var(--primary-soft)" : "var(--surface)" }}
    >
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
           style={{ background: isShared ? "var(--primary)" : "var(--surface-2)",
                    color: isShared ? "white" : "var(--text-muted)" }}>
        {isShared ? <Users className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold" style={{ color: isShared ? "var(--primary-ink)" : "var(--text-main)" }}>
          {isShared ? `Shared with ${fam.name}` : "Private to you"}
        </div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          {isShared
            ? `${fam.members.length} family member(s) can see this prescription.`
            : <>Other family members can't see this. <Link to="/family" className="hover:underline" style={{ color: "var(--primary-ink)" }}>Manage family →</Link></>}
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="family-share-button"
        className="h-9 px-3 rounded-lg text-sm font-semibold border whitespace-nowrap disabled:opacity-60"
        style={{ borderColor: "var(--border-subtle)",
                 background: "var(--surface)",
                 color: "var(--text-main)" }}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isShared ? "Make private" : "Share with family")}
      </button>
    </div>
  );
}
